import React, { FunctionComponent, PropsWithChildren } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import IconButton from "../button/IconButton";
import FlexRowCenter from "../flex/FlexRowCenter";
import FlexColumn from "../flex/FlexColumn";
import { useLocale } from "designSystem/locale";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import OverflowText from "../text/OverflowText";

export interface PaneHeaderProps extends PropsWithChildren {
	/** include back button */
	back?: boolean;

	/** callback when back button is clicked */
	onBack?: () => void;

	/** callback when close button is clicked */
	onClose?: () => void;

	/** pane title */
	title?: string;

	/** localization resource for pane title */
	titleRes?: string;

	/** parameters for localization resource for pane title */
	titleResParams?: Array<number | string>;

	/** pane sub title */
	subTitle?: string;

	/** localization resource for pane sub title */
	subTitleRes?: string;

	/** parameters for localization resource for pane sub title */
	subTitleResParams?: Array<number | string>;
}

const SubTitleText = styled(OverflowText)(({ theme }) => ({
	color: theme.palette.text.secondary,
}));

const PANE_HEADER_HEIGHT = 48; //px
const PANE_HEADER_HEIGHT_WITH_SUBTITLE = 64; //px

const PaneHeader: FunctionComponent<PaneHeaderProps> = (props) => {
	const {
		back,
		children,
		onBack,
		onClose,
		title,
		titleRes,
		titleResParams,
		subTitle,
		subTitleRes,
		subTitleResParams,
	} = props;

	const titleFinal = title || useLocale()
		.resource(titleRes, titleResParams);

	const subTitleFinal = subTitle || useLocale()
		.resource(subTitleRes, subTitleResParams);

	const hasTitle = titleFinal || subTitleFinal;

	return (
		<FlexRowCenter
			borderBottom={STANDARD_BORDER_STYLE}
			flexShrink={0}
			height={subTitleFinal ? PANE_HEADER_HEIGHT_WITH_SUBTITLE : PANE_HEADER_HEIGHT}
			px={3}
			width="100%"
		>
			{back && (
				<IconButton
					aria-label="back"
					onClick={onBack}
					size="large"
					style={{ marginRight: 4 }}
				>
					<ArrowForwardIcon style={{ transform: "rotate(180deg)" }} />
				</IconButton>
			)}
			{hasTitle && (
				<FlexColumn flexGrow={1} overflow="hidden">
					{titleFinal && (
						<OverflowText
							bold
							grow
							size={16}
							text={titleFinal}
							variant="h4"
						/>
					)}{subTitleFinal && (
						<SubTitleText
							grow
							lineHeight={14}
							semiBold
							size={12}
							text={subTitleFinal}
							variant="subtitle2"
						/>
					)}
				</FlexColumn>
			)}
			{children}
			{onClose && (
				<IconButton
					aria-label="close"
					onClick={onClose}
					size="large"
					style={{ marginLeft: 4 }}
				>
					<CloseIcon />
				</IconButton>
			)}
		</FlexRowCenter>
	);
};

export default PaneHeader;